import { useCallback, useEffect, useRef, useState } from "react";
import axios from "axios";
import { toast, Toaster } from "sonner";
import { Check, X, Trash2, LogOut, ExternalLink, RefreshCw } from "lucide-react";

const API = process.env.REACT_APP_BACKEND_URL;
// REMINDER: DO NOT HARDCODE THE URL, OR ADD ANY FALLBACKS OR REDIRECT URLS, THIS BREAKS THE AUTH
axios.defaults.withCredentials = true;

const AdminPage = () => {
  const [phase, setPhase] = useState("checking"); // checking | login | ready | forbidden
  const [user, setUser] = useState(null);
  const [tab, setTab] = useState("pending"); // pending | approved | rejected
  const [subs, setSubs] = useState([]);
  const [loadingSubs, setLoadingSubs] = useState(false);
  const processedHashRef = useRef(false);

  const load = useCallback(async (status) => {
    setLoadingSubs(true);
    try {
      const { data } = await axios.get(`${API}/api/admin/submissions`, { params: { status } });
      setSubs(data.submissions || []);
    } catch (err) {
      if (err?.response?.status === 401) setPhase("login");
      else if (err?.response?.status === 403) setPhase("forbidden");
      else toast.error("Failed to load submissions");
    } finally {
      setLoadingSubs(false);
    }
  }, []);

  // Exchange session_id from OAuth callback (URL fragment) once.
  useEffect(() => {
    const hash = window.location.hash || "";
    if (!hash.includes("session_id=") || processedHashRef.current) return;
    processedHashRef.current = true;
    const sid = new URLSearchParams(hash.replace(/^#/, "")).get("session_id");
    if (!sid) return;
    (async () => {
      try {
        await axios.post(`${API}/api/auth/session`, { session_id: sid });
        window.history.replaceState(null, "", "/admin");
      } catch {
        toast.error("Auth exchange failed");
      }
    })();
  }, []);

  // Bootstrap: check /auth/me
  useEffect(() => {
    (async () => {
      try {
        const { data } = await axios.get(`${API}/api/auth/me`);
        setUser(data);
        if (!data.is_admin) {
          setPhase("forbidden");
        } else {
          setPhase("ready");
        }
      } catch {
        setPhase("login");
      }
    })();
  }, []);

  useEffect(() => {
    if (phase === "ready") load(tab);
  }, [phase, tab, load]);

  const login = () => {
    // REMINDER: DO NOT HARDCODE THE URL, OR ADD ANY FALLBACKS OR REDIRECT URLS, THIS BREAKS THE AUTH
    const redirect = `${window.location.origin}/admin`;
    window.location.href = `https://auth.emergentagent.com/?redirect=${encodeURIComponent(redirect)}`;
  };

  const logout = async () => {
    try {
      await axios.post(`${API}/api/auth/logout`);
    } catch { /* ignore */ }
    setUser(null);
    setPhase("login");
  };

  const decide = async (id, action) => {
    try {
      await axios.post(`${API}/api/admin/submissions/${id}/${action}`);
      toast.success(action === "approve" ? "Approved — added to rotation." : "Rejected.");
      load(tab);
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Action failed");
    }
  };

  const del = async (id) => {
    if (!window.confirm("Delete this submission permanently?")) return;
    try {
      await axios.delete(`${API}/api/admin/submissions/${id}`);
      toast.success("Deleted.");
      load(tab);
    } catch {
      toast.error("Delete failed");
    }
  };

  return (
    <div className="min-h-screen bg-[#050409] text-white" data-testid="admin-page">
      <Toaster position="top-center" toastOptions={{ style: { background: "#0a0714", border: "1px solid rgba(20,241,149,0.5)", color: "#fff" } }} />
      <div className="max-w-4xl mx-auto px-4 sm:px-8 py-10">
        <header className="flex items-center justify-between flex-wrap gap-4">
          <div>
            <p className="font-bebas tracking-[0.35em] text-[#9945FF] text-sm">CURB CONTROL ROOM</p>
            <h1 className="font-marker text-4xl sm:text-5xl gradient-sol text-glow-green -rotate-1 inline-block mt-1">
              STORM SETLIST QUEUE
            </h1>
          </div>
          {user && (
            <div className="flex items-center gap-3">
              {user.picture && <img src={user.picture} alt="avatar" className="w-9 h-9 rounded-full border border-[#9945FF]/50" />}
              <div className="text-right text-sm">
                <p className="text-white font-semibold" data-testid="admin-user-name">{user.name}</p>
                <p className="text-zinc-500 text-xs">{user.email}</p>
              </div>
              <button onClick={logout} className="text-zinc-400 hover:text-[#14F195] p-2 transition-colors" data-testid="admin-logout-button" aria-label="Sign out">
                <LogOut size={18} />
              </button>
            </div>
          )}
        </header>

        {phase === "checking" && (
          <p className="mt-16 text-center text-zinc-500 font-bebas tracking-widest">READING THE SKIES…</p>
        )}

        {phase === "login" && (
          <div className="mt-16 max-w-sm mx-auto text-center rounded-2xl border border-[#9945FF]/40 bg-[#0a0714] p-8" data-testid="admin-login-panel">
            <p className="font-bebas tracking-[0.32em] text-[#14F195] text-sm">RESTRICTED</p>
            <h2 className="font-marker text-3xl gradient-sol mt-2">CURB ONLY</h2>
            <p className="text-zinc-400 text-sm mt-3">
              Sign in with your admin Google account to review pending submissions.
            </p>
            <button
              onClick={login}
              data-testid="admin-google-login-button"
              className="mt-6 w-full btn-buy font-bebas tracking-widest text-lg rounded-full py-2.5"
            >
              SIGN IN WITH GOOGLE
            </button>
          </div>
        )}

        {phase === "forbidden" && (
          <div className="mt-16 max-w-sm mx-auto text-center rounded-2xl border border-red-500/40 bg-[#0a0714] p-8" data-testid="admin-forbidden-panel">
            <p className="font-bebas tracking-[0.32em] text-red-400 text-sm">403 · NOT ADMIN</p>
            <h2 className="font-marker text-2xl text-white mt-2">Access denied.</h2>
            <p className="text-zinc-400 text-sm mt-3">
              This Google account isn&apos;t on the admin allowlist. Ask the owner to add <span className="text-[#14F195]">{user?.email}</span> to <code>ADMIN_EMAILS</code>.
            </p>
            <button onClick={logout} className="mt-5 text-sm text-zinc-400 hover:text-white underline">
              Sign out
            </button>
          </div>
        )}

        {phase === "ready" && (
          <>
            <div className="mt-8 flex items-center gap-2 flex-wrap">
              {["pending", "approved", "rejected"].map((t) => (
                <button
                  key={t}
                  onClick={() => setTab(t)}
                  data-testid={`admin-tab-${t}`}
                  className={`px-4 py-1.5 rounded-full text-xs font-bebas tracking-[0.25em] border transition-colors ${
                    tab === t
                      ? "border-[#14F195] text-[#050409] bg-gradient-to-r from-[#14F195] to-[#9945FF]"
                      : "border-[#9945FF]/40 text-zinc-300 hover:border-[#9945FF]"
                  }`}
                >
                  {t.toUpperCase()}
                </button>
              ))}
              <button
                onClick={() => load(tab)}
                aria-label="Refresh"
                className="ml-auto text-zinc-400 hover:text-[#14F195] p-2 transition-colors"
                data-testid="admin-refresh-button"
              >
                <RefreshCw size={16} className={loadingSubs ? "animate-spin" : ""} />
              </button>
            </div>

            <ul className="mt-6 space-y-3" data-testid="admin-submissions-list">
              {loadingSubs && subs.length === 0 && (
                <p className="text-zinc-500 text-sm">Loading…</p>
              )}
              {!loadingSubs && subs.length === 0 && (
                <p className="text-zinc-500 text-sm" data-testid="admin-empty-state">
                  Nothing here. The queue is calm — for now.
                </p>
              )}
              {subs.map((s) => (
                <li
                  key={s.submission_id}
                  data-testid={`admin-sub-${s.submission_id}`}
                  className="rounded-xl border border-[#9945FF]/30 bg-[#0a0714] p-4 flex items-start gap-4 flex-wrap"
                >
                  <div className="flex-1 min-w-[220px]">
                    <p className="font-marker text-lg text-[#14F195]">{s.track_name}</p>
                    <p className="text-zinc-300 text-sm">by {s.artist_name}</p>
                    {s.submitter && <p className="text-zinc-500 text-xs mt-1">submitted by {s.submitter}</p>}
                    <a
                      href={s.track_url}
                      target="_blank"
                      rel="noopener noreferrer"
                      className="mt-2 inline-flex items-center gap-1 text-xs text-[#9945FF] hover:text-[#14F195] break-all"
                    >
                      <ExternalLink size={12} /> {s.track_url}
                    </a>
                    <p className="text-zinc-600 text-[11px] mt-1">
                      {new Date(s.created_at).toLocaleString()} · rights confirmed:{" "}
                      <span className={s.rights_confirmed ? "text-[#14F195]" : "text-red-400"}>
                        {s.rights_confirmed ? "yes" : "NO"}
                      </span>
                    </p>
                    {s.status !== "pending" && (
                      <p className="text-zinc-600 text-[11px] mt-1">
                        {s.status} by {s.reviewed_by} · {s.reviewed_at ? new Date(s.reviewed_at).toLocaleString() : ""}
                      </p>
                    )}
                  </div>
                  <div className="flex items-center gap-2">
                    {s.status === "pending" && (
                      <>
                        <button
                          onClick={() => decide(s.submission_id, "approve")}
                          data-testid={`admin-approve-${s.submission_id}`}
                          className="btn-buy rounded-full px-3 py-1.5 text-xs font-bebas tracking-[0.2em] flex items-center gap-1"
                        >
                          <Check size={14} /> APPROVE
                        </button>
                        <button
                          onClick={() => decide(s.submission_id, "reject")}
                          data-testid={`admin-reject-${s.submission_id}`}
                          className="rounded-full px-3 py-1.5 text-xs font-bebas tracking-[0.2em] border border-red-500/60 text-red-400 hover:bg-red-500/10 flex items-center gap-1"
                        >
                          <X size={14} /> REJECT
                        </button>
                      </>
                    )}
                    <button
                      onClick={() => del(s.submission_id)}
                      data-testid={`admin-delete-${s.submission_id}`}
                      className="text-zinc-500 hover:text-red-400 p-2 transition-colors"
                      aria-label="Delete"
                    >
                      <Trash2 size={14} />
                    </button>
                  </div>
                </li>
              ))}
            </ul>
          </>
        )}
      </div>
    </div>
  );
};

export default AdminPage;
